import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import {
  User, Calendar as CalendarIcon, Briefcase, DollarSign, Settings, LogOut,
  Save, Plus, Trash2, Upload, ChevronRight, Clock, MapPin, AlertCircle, X, FileText, Radio, Bell, CheckCircle
} from 'lucide-react';

const StandInDashboard = () => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const navItems = [
    { path: '/standin', label: 'Profile', icon: User },
    { path: '/standin/today', label: 'Today', icon: Clock },
    { path: '/standin/availability', label: 'Availability', icon: CalendarIcon },
    { path: '/standin/bookings', label: 'Bookings', icon: Briefcase },
    { path: '/standin/productions', label: 'Productions', icon: Briefcase },
    { path: '/standin/settings', label: 'Settings', icon: Settings },
  ];

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch (e) { /* silent */ }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch (e) { console.error(e); }
  };

  const toggleNotifications = () => {
    if (!showNotifications) fetchNotifications();
    setShowNotifications(!showNotifications);
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#121212] border-r border-white/5 fixed h-screen overflow-y-auto">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-24 w-auto"
            />
          </Link>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
              <span className="text-[#D4AF37] font-semibold">{user?.full_name?.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.full_name}</p>
              <p className="text-white/40 text-sm">Stand-In</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        {/* Notification Bell */}
        <div className="flex justify-end mb-4 relative">
          <button
            onClick={toggleNotifications}
            className="relative p-2 rounded-lg bg-[#121212] border border-white/10 hover:border-[#D4AF37]/30 transition-colors"
            data-testid="notification-bell"
          >
            <Bell className="w-5 h-5 text-white/60" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold" data-testid="notification-badge">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {showNotifications && (
            <div className="absolute top-12 right-0 w-96 bg-[#121212] border border-white/10 rounded-xl shadow-2xl z-50 max-h-[70vh] overflow-hidden" data-testid="notification-dropdown">
              <div className="flex items-center justify-between p-4 border-b border-white/5">
                <h3 className="text-white font-semibold">Notifications</h3>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-[#D4AF37] text-xs hover:underline" data-testid="mark-all-read">
                    Mark all read
                  </button>
                )}
              </div>
              <div className="overflow-y-auto max-h-[60vh]">
                {notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="w-8 h-8 text-white/20 mx-auto mb-2" />
                    <p className="text-white/40 text-sm">No notifications yet</p>
                  </div>
                ) : (
                  notifications.map(n => (
                    <div
                      key={n.id}
                      className={`p-4 border-b border-white/5 ${!n.read ? 'bg-[#D4AF37]/5' : ''}`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                          n.notification_type === 'cancellation' ? 'bg-red-500' :
                          n.notification_type === 'reschedule' ? 'bg-blue-500' :
                          n.notification_type === 'booking' ? 'bg-green-500' :
                          'bg-[#D4AF37]'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm">{n.title}</p>
                          <p className="text-white/60 text-xs mt-0.5">{n.message}</p>
                          <p className="text-white/30 text-[10px] mt-1">
                            {new Date(n.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {user?.status === 'pending' && (
          <div className="mb-6 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-[#F59E0B] font-medium">Account Pending Approval</p>
              <p className="text-[#F59E0B]/70 text-sm">Please complete your profile. An admin will review and approve your account.</p>
            </div>
          </div>
        )}
        
        <Routes>
          <Route index element={<ProfileEditor token={token} />} />
          <Route path="today" element={<TodayCallSheet token={token} />} />
          <Route path="availability" element={<AvailabilityManager token={token} />} />
          <Route path="bookings" element={<BookingsView token={token} />} />
          <Route path="productions" element={<ProductionsView token={token} />} />
          <Route path="settings" element={<SettingsView />} />
        </Routes>
      </main>
    </div>
  );
};

// Today's Call Sheet View
const TodayCallSheet = ({ token }) => {
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availChecks, setAvailChecks] = useState([]);
  const [responding, setResponding] = useState(null);

  useEffect(() => {
    fetchTodaySheets();
    fetchAvailChecks();
  }, []);

  const fetchTodaySheets = async () => {
    try {
      const res = await axios.get(`${API}/call-sheets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSheets(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchAvailChecks = async () => {
    try {
      const res = await axios.get(`${API}/standin/availability-checks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailChecks(res.data);
    } catch (e) { console.error(e); }
  };

  const respondToCheck = async (checkId, available) => {
    setResponding(checkId);
    try {
      await axios.post(`${API}/standin/respond-availability/${checkId}?available=${available}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(available ? "Marked as available — production notified!" : "Marked as unavailable");
      fetchAvailChecks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to respond');
    } finally {
      setResponding(null);
    }
  };

  const handleArrive = async (sheetId) => {
    try {
      await axios.put(`${API}/call-sheets/${sheetId}/arrive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("You've been marked as arrived!");
      fetchTodaySheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark arrival');
    }
  };

  const handleWrap = async (sheetId) => {
    try {
      await axios.put(`${API}/call-sheets/${sheetId}/wrap`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Wrap recorded. Great work today!");
      fetchTodaySheets();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark wrap');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  // Separate today's and upcoming sheets
  const today = new Date().toISOString().split('T')[0];
  const todaySheets = sheets.filter(s => s.date === today);
  const upcomingSheets = sheets.filter(s => s.date > today);
  const pastSheets = sheets.filter(s => s.date < today).slice(0, 5);

  return (
    <div className="max-w-3xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Today</h1>
      <p className="text-white/60 mb-8">Your call sheets, availability requests, and daily check-in</p>

      {/* Availability Check Requests */}
      {availChecks.filter(c => c.status === 'pending').length > 0 && (
        <div className="mb-8">
          <h2 className="text-white/60 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#F59E0B] animate-pulse" />
            Availability Checks — Please Respond
          </h2>
          <div className="space-y-3">
            {availChecks.filter(c => c.status === 'pending').map(check => {
              const formatDate = (d) => {
                const date = new Date(d + 'T00:00:00');
                const day = date.toLocaleDateString('en-GB', { weekday: 'long' });
                const num = date.getDate();
                const suffix = num === 1 || num === 21 || num === 31 ? 'st' : num === 2 || num === 22 ? 'nd' : num === 3 || num === 23 ? 'rd' : 'th';
                const month = date.toLocaleDateString('en-GB', { month: 'long' });
                return `${day} the ${num}${suffix} ${month}`;
              };

              return (
                <div key={check.id} className="bg-[#121212] border border-[#F59E0B]/30 rounded-xl overflow-hidden" data-testid={`avail-check-${check.id}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <p className="text-[#F59E0B] text-xs font-semibold uppercase tracking-wider mb-1">Availability Check</p>
                        <h3 className="text-white font-semibold text-lg">{check.production_name}</h3>
                      </div>
                      <span className="px-2 py-1 bg-[#F59E0B]/20 text-[#F59E0B] text-xs rounded font-semibold uppercase">
                        Awaiting Response
                      </span>
                    </div>
                    <div className="space-y-2 mb-4">
                      {check.dates_needed?.map((d, i) => (
                        <div key={i} className="flex items-center gap-2 text-white/80 text-sm">
                          <CalendarIcon className="w-4 h-4 text-[#D4AF37]" />
                          <span>{formatDate(d)}</span>
                        </div>
                      ))}
                      {check.location && (
                        <div className="flex items-center gap-2 text-white/80 text-sm">
                          <MapPin className="w-4 h-4 text-[#D4AF37]" />
                          <span>{check.location}</span>
                        </div>
                      )}
                      {check.pay_rate && (
                        <div className="flex items-center gap-2 text-[#D4AF37] font-semibold text-sm">
                          <span className="w-4 h-4 flex items-center justify-center text-xs">£</span>
                          <span>£{check.pay_rate} day rate{check.expected_hours ? ` ${check.expected_hours} hours` : ''}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => respondToCheck(check.id, true)}
                        disabled={responding === check.id}
                        className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-semibold text-base"
                        data-testid={`respond-available-${check.id}`}
                      >
                        <CheckCircle className="w-5 h-5 mr-2" />
                        {responding === check.id ? 'Sending...' : "I'm Available"}
                      </Button>
                      <Button
                        onClick={() => respondToCheck(check.id, false)}
                        disabled={responding === check.id}
                        variant="outline"
                        className="flex-1 h-12 border-red-500/30 text-red-400 hover:bg-red-500/10 font-semibold text-base"
                        data-testid={`respond-unavailable-${check.id}`}
                      >
                        <X className="w-5 h-5 mr-2" />
                        Unavailable
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Past responses */}
      {availChecks.filter(c => c.status !== 'pending').length > 0 && (
        <div className="mb-8">
          <h2 className="text-white/60 text-xs uppercase tracking-wider mb-3">Previous Requests</h2>
          <div className="space-y-2">
            {availChecks.filter(c => c.status !== 'pending').slice(0, 5).map(check => (
              <div key={check.id} className="bg-[#121212] border border-white/5 rounded-xl p-4 flex items-center justify-between opacity-70">
                <div>
                  <span className="text-white font-medium text-sm">{check.production_name}</span>
                  <span className="text-white/40 text-sm ml-2">{check.dates_needed?.[0] || ''}</span>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded ${
                  check.status === 'responded_available' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {check.status === 'responded_available' ? 'Available' : 'Unavailable'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today's Call Sheets */}
      {todaySheets.length === 0 && upcomingSheets.length === 0 && pastSheets.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <Clock className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No call sheets yet. You'll see your daily schedule here when a Crowd 2nd publishes one.</p>
        </div>
      ) : (
        <>
          {todaySheets.length > 0 && (
            <div className="mb-8">
              <h2 className="text-white/60 text-xs uppercase tracking-wider mb-3">Today — {today}</h2>
              {todaySheets.map(sheet => {
                const myEntry = sheet.my_entry;
                return (
                  <div key={sheet.id} className="bg-[#121212] border border-[#D4AF37]/30 rounded-xl overflow-hidden" data-testid={`today-sheet-${sheet.id}`}>
                    {/* Header */}
                    <div className="p-6 bg-gradient-to-r from-[#D4AF37]/10 to-transparent">
                      <h3 className="text-white font-semibold text-xl">{sheet.production_name}</h3>
                      {myEntry?.call_time && (
                        <p className="text-[#D4AF37] text-lg font-bold mt-1">
                          <Clock className="w-4 h-4 inline mr-1" />
                          Call Time: {myEntry.call_time}
                        </p>
                      )}
                    </div>

                    {/* Details */}
                    <div className="p-6 space-y-3">
                      {sheet.base_location && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wider">Base Location</p>
                            <p className="text-white">{sheet.base_location}</p>
                          </div>
                        </div>
                      )}
                      {sheet.set_location && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wider">Set Location</p>
                            <p className="text-white">{sheet.set_location}</p>
                          </div>
                        </div>
                      )}
                      {sheet.parking_location && (
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-white/30 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wider">Parking</p>
                            <p className="text-white">{sheet.parking_location}</p>
                          </div>
                        </div>
                      )}
                      {sheet.colour_cover && (
                        <div className="flex items-start gap-3">
                          <div className="w-4 h-4 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white/40 text-xs uppercase tracking-wider">Colour Cover</p>
                            <p className="text-white">{sheet.colour_cover}</p>
                          </div>
                        </div>
                      )}
                      {sheet.notes && (
                        <div className="p-3 bg-[#0A0A0A] rounded-lg">
                          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Notes</p>
                          <p className="text-white/80 text-sm">{sheet.notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {myEntry && (
                      <div className="p-6 pt-0">
                        {myEntry.status === 'pending' && (
                          <Button
                            onClick={() => handleArrive(sheet.id)}
                            className="w-full h-14 text-lg font-bold gold-gradient text-black hover:brightness-110"
                            data-testid="arrive-btn"
                          >
                            <CheckCircle className="w-5 h-5 mr-2" />
                            I've Arrived
                          </Button>
                        )}
                        {myEntry.status === 'arrived' && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                              <span className="text-green-400 font-medium">Arrived at {myEntry.arrived_at}</span>
                            </div>
                            <Button
                              onClick={() => handleWrap(sheet.id)}
                              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                              data-testid="wrap-btn"
                            >
                              <Clock className="w-4 h-4 mr-2" />
                              That's a Wrap
                            </Button>
                          </div>
                        )}
                        {myEntry.status === 'wrapped' && (
                          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-center">
                            <p className="text-blue-400 font-medium">Wrapped at {myEntry.wrapped_at}</p>
                            <p className="text-white/40 text-sm mt-1">Arrived: {myEntry.arrived_at} — Wrapped: {myEntry.wrapped_at}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Upcoming */}
          {upcomingSheets.length > 0 && (
            <div className="mb-8">
              <h2 className="text-white/60 text-xs uppercase tracking-wider mb-3">Upcoming</h2>
              <div className="space-y-3">
                {upcomingSheets.map(sheet => (
                  <div key={sheet.id} className="bg-[#121212] border border-white/5 rounded-xl p-5" data-testid={`upcoming-sheet-${sheet.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-white font-semibold">{sheet.production_name}</h3>
                        <p className="text-[#D4AF37] text-sm mt-0.5">{sheet.date}</p>
                        <div className="flex items-center gap-4 mt-2 text-white/40 text-sm">
                          {sheet.my_entry?.call_time && <span><Clock className="w-3.5 h-3.5 inline mr-1" />Call: {sheet.my_entry.call_time}</span>}
                          {sheet.base_location && <span><MapPin className="w-3.5 h-3.5 inline mr-1" />{sheet.base_location}</span>}
                        </div>
                        {sheet.colour_cover && <p className="text-white/30 text-sm mt-1">Cover: {sheet.colour_cover}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent/Past */}
          {pastSheets.length > 0 && (
            <div>
              <h2 className="text-white/60 text-xs uppercase tracking-wider mb-3">Recent</h2>
              <div className="space-y-2">
                {pastSheets.map(sheet => (
                  <div key={sheet.id} className="bg-[#121212] border border-white/5 rounded-xl p-4 opacity-60">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-white font-medium text-sm">{sheet.production_name}</span>
                        <span className="text-white/40 text-sm ml-2">{sheet.date}</span>
                      </div>
                      {sheet.my_entry && (
                        <span className="text-white/30 text-xs">
                          {sheet.my_entry.arrived_at && `${sheet.my_entry.arrived_at}`}
                          {sheet.my_entry.wrapped_at && ` — ${sheet.my_entry.wrapped_at}`}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Experience Level Options
const experienceLevels = [
  { value: 'no_experience', label: 'No Experience' },
  { value: 'some_extra_experience', label: 'Some Extra/Background Experience' },
  { value: 'extensive_extra_experience', label: 'Extensive Extra/Background Experience' },
  { value: 'some_standin_experience', label: 'Some Stand-In Experience' },
  { value: 'extensive_standin_experience', label: 'Extensive Film Stand-In Experience' }
];

// Profile Editor Component - Comprehensive Form
const ProfileEditor = ({ token }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    // Basic Info
    gender: '',
    hair_color: '',
    skin_tone: '',
    experience_level: '',
    // Measurements
    height_ft: '',
    height_in: '',
    chest_bust: '',
    waist: '',
    hips: '',
    inside_leg: '',
    shoe_size: '',
    neck: '',
    // Rate
    preferred_rate: 250,
    // Photos
    headshot_url: '',
    full_body_url: '',
    // Personal Details (for admin/payment)
    date_of_birth: '',
    phone_number: '',
    contact_email: '',
    address: '',
    national_insurance: '',
    bank_name: '',
    account_number: '',
    sort_code: '',
    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    // Right to Work
    right_to_work_document_url: ''
  });

  const [experiences, setExperiences] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [radioPhrases, setRadioPhrases] = useState({ phrase_1: '', phrase_2: '', phrase_3: '', phrase_4: '' });
  const [savingPhrases, setSavingPhrases] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const [profileRes, filesRes, phrasesRes] = await Promise.all([
        axios.get(`${API}/standin/profile`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/user/files`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/standin/radio-phrases`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
      ]);
      const data = profileRes.data;
      setProfile(data);
      setFormData({
        gender: data.gender || '',
        hair_color: data.hair_color || '',
        skin_tone: data.skin_tone || '',
        experience_level: data.experience_level || '',
        height_ft: data.measurements?.height_ft || '',
        height_in: data.measurements?.height_in || '',
        chest_bust: data.measurements?.chest_bust || '',
        waist: data.measurements?.waist || '',
        hips: data.measurements?.hips || '',
        inside_leg: data.measurements?.inside_leg || '',
        shoe_size: data.measurements?.shoe_size || '',
        neck: data.measurements?.neck || '',
        preferred_rate: data.preferred_rate || 250,
        headshot_url: data.headshot_url || '',
        full_body_url: data.full_body_url || '',
        address: data.address || '',
        date_of_birth: data.date_of_birth || '',
        phone_number: data.phone_number || '',
        contact_email: data.contact_email || '',
        national_insurance: data.national_insurance || '',
        bank_name: data.bank_name || '',
        account_number: data.account_number || '',
        sort_code: data.sort_code || '',
        emergency_contact_name: data.emergency_contact_name || '',
        emergency_contact_phone: data.emergency_contact_phone || '',
        right_to_work_document_url: data.right_to_work_document_url || ''
      });
      setExperiences(data.experiences || []);
      setPhotos(filesRes.data.filter(f => f.file_type === 'image'));
      setDocuments(filesRes.data.filter(f => f.file_type === 'document'));
      const p = phrasesRes.data || {};
      setRadioPhrases({ phrase_1: p.phrase_1 || '', phrase_2: p.phrase_2 || '', phrase_3: p.phrase_3 || '', phrase_4: p.phrase_4 || '' });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.gender) {
      toast.error('Gender is required');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/standin/profile`, {
        gender: formData.gender || null,
        hair_color: formData.hair_color || null,
        skin_tone: formData.skin_tone || null,
        experience_level: formData.experience_level || null,
        measurements: {
          height_ft: formData.height_ft ? parseInt(formData.height_ft) : null,
          height_in: formData.height_in ? parseFloat(formData.height_in) : null,
          chest_bust: formData.chest_bust ? parseFloat(formData.chest_bust) : null,
          waist: formData.waist ? parseFloat(formData.waist) : null,
          hips: formData.hips ? parseFloat(formData.hips) : null,
          inside_leg: formData.inside_leg ? parseFloat(formData.inside_leg) : null,
          shoe_size: formData.shoe_size || null,
          neck: formData.neck ? parseFloat(formData.neck) : null
        },
        preferred_rate: parseFloat(formData.preferred_rate),
        headshot_url: formData.headshot_url || null,
        full_body_url: formData.full_body_url || null,
        experiences: experiences,
        address: formData.address || null,
        date_of_birth: formData.date_of_birth || null,
        phone_number: formData.phone_number || null,
        contact_email: formData.contact_email || null,
        national_insurance: formData.national_insurance || null,
        bank_name: formData.bank_name || null,
        account_number: formData.account_number || null,
        sort_code: formData.sort_code || null,
        emergency_contact_name: formData.emergency_contact_name || null,
        emergency_contact_phone: formData.emergency_contact_phone || null,
        right_to_work_document_url: formData.right_to_work_document_url || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile saved successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const addExperience = () => {
    setExperiences([...experiences, {
      actor_name: '',
      production_name: '',
      crowd_2nd: '',
      first_ad: '',
      days_worked: 1
    }]);
  };

  const updateExperience = (index, field, value) => {
    const updated = [...experiences];
    updated[index][field] = value;
    setExperiences(updated);
  };

  const removeExperience = (index) => {
    setExperiences(experiences.filter((_, i) => i !== index));
  };

  const handlePhotoUpload = async (e, slotIndex) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (photos.length >= 6 && slotIndex === null) {
      toast.error('Maximum 6 photos allowed');
      return;
    }
    setUploadingSlot(slotIndex !== null ? slotIndex : 'new');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setPhotos(prev => [...prev, { ...res.data, storage_path: res.data.path, file_type: 'image', original_filename: res.data.filename }]);
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleDocUpload = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDoc(docType);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/document?doc_type=${docType}`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setDocuments(prev => [...prev, { ...res.data, storage_path: res.data.path, file_type: 'document', doc_type: docType, original_filename: res.data.filename }]);
      toast.success('Document uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDeleteFile = async (fileId, type) => {
    try {
      await axios.delete(`${API}/files/${fileId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (type === 'image') setPhotos(prev => prev.filter(f => f.id !== fileId));
      else setDocuments(prev => prev.filter(f => f.id !== fileId));
      toast.success('File removed');
    } catch (error) {
      toast.error('Failed to remove file');
    }
  };

  const handleSaveRadioPhrases = async () => {
    if (!radioPhrases.phrase_1 || !radioPhrases.phrase_2 || !radioPhrases.phrase_3 || !radioPhrases.phrase_4) {
      toast.error('Please fill in all 4 radio phrases');
      return;
    }
    setSavingPhrases(true);
    try {
      await axios.put(`${API}/standin/radio-phrases`, radioPhrases, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Radio phrases saved');
    } catch (error) {
      toast.error('Failed to save radio phrases');
    } finally {
      setSavingPhrases(false);
    }
  };

  const getFileUrl = (storagePath) => `${API}/files/${storagePath}?auth=${token}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'measurements', label: 'Measurements' },
    { id: 'photos', label: 'Photos' },
    { id: 'documents', label: 'Documents' },
    { id: 'experience', label: 'Experience' },
    { id: 'payment', label: 'Payment & ID' }
  ];

  return (
    <div className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white">My Profile</h1>
          <p className="text-white/60 mt-1">Complete all sections for admin approval</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="gold-gradient text-black font-semibold hover:brightness-110"
          data-testid="save-profile-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id 
                ? 'bg-[#D4AF37] text-black' 
                : 'bg-[#121212] text-white/60 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Basic Info Tab */}
      {activeTab === 'basic' && (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-6">Basic Information</h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label className="text-white/80 mb-2 block">Gender *</Label>
              <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="gender-select">
                  <SelectValue placeholder="Select gender..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="non_binary">Non-Binary</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Experience Level *</Label>
              <Select value={formData.experience_level} onValueChange={(v) => setFormData({ ...formData, experience_level: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="experience-level-select">
                  <SelectValue placeholder="Select experience..." />
                </SelectTrigger>
                <SelectContent>
                  {experienceLevels.map(level => (
                    <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Hair Colour *</Label>
              <Select value={formData.hair_color} onValueChange={(v) => setFormData({ ...formData, hair_color: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="hair-color-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="black">Black</SelectItem>
                  <SelectItem value="brown">Brown</SelectItem>
                  <SelectItem value="blonde">Blonde</SelectItem>
                  <SelectItem value="red">Red / Auburn</SelectItem>
                  <SelectItem value="grey">Grey</SelectItem>
                  <SelectItem value="white">White</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Skin Tone / Ethnicity *</Label>
              <Select value={formData.skin_tone} onValueChange={(v) => setFormData({ ...formData, skin_tone: v })}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="skin-tone-select">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="very_fair">Very Fair</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="olive">Olive</SelectItem>
                  <SelectItem value="tan">Tan</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="very_dark">Very Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-white/80 mb-2 block">Preferred Day Rate (£)</Label>
              <Input
                type="number"
                value={formData.preferred_rate}
                onChange={(e) => setFormData({ ...formData, preferred_rate: e.target.value })}
                placeholder="250"
                className="bg-[#0A0A0A] border-white/10 text-white h-12 max-w-xs"
                data-testid="rate-input"
              />
              <p className="text-white/40 text-sm mt-1">Standard UK rate is £250/day</p>
            </div>
          </div>
        </div>
      )}

      {/* Measurements Tab */}
      {activeTab === 'measurements' && (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-6">Measurements</h2>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-white/80 mb-2 block">Height (ft) *</Label>
              <Input
                type="number"
                value={formData.height_ft}
                onChange={(e) => setFormData({ ...formData, height_ft: e.target.value })}
                placeholder="5"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="height-ft-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Height (in) *</Label>
              <Input
                type="number"
                step="0.5"
                value={formData.height_in}
                onChange={(e) => setFormData({ ...formData, height_in: e.target.value })}
                placeholder="10"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="height-in-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Chest/Bust *</Label>
              <Input
                type="number"
                value={formData.chest_bust}
                onChange={(e) => setFormData({ ...formData, chest_bust: e.target.value })}
                placeholder="38"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="chest-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Waist/Belly *</Label>
              <Input
                type="number"
                value={formData.waist}
                onChange={(e) => setFormData({ ...formData, waist: e.target.value })}
                placeholder="32"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="waist-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Hips</Label>
              <Input
                type="number"
                value={formData.hips}
                onChange={(e) => setFormData({ ...formData, hips: e.target.value })}
                placeholder="36"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="hips-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Inside Leg *</Label>
              <Input
                type="number"
                value={formData.inside_leg}
                onChange={(e) => setFormData({ ...formData, inside_leg: e.target.value })}
                placeholder="32"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="inside-leg-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Neck</Label>
              <Input
                type="number"
                value={formData.neck}
                onChange={(e) => setFormData({ ...formData, neck: e.target.value })}
                placeholder="15"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="neck-input"
              />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Shoe Size (UK) *</Label>
              <Input
                value={formData.shoe_size}
                onChange={(e) => setFormData({ ...formData, shoe_size: e.target.value })}
                placeholder="10"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="shoe-size-input"
              />
            </div>
          </div>
          <p className="text-white/40 text-sm mt-4">* All measurements in inches unless specified</p>
        </div>
      )}

      {/* Photos Tab - Upload Cards */}
      {activeTab === 'photos' && (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-2">Profile Photos</h2>
          <p className="text-white/40 text-sm mb-6">Upload up to 6 photos. Include a clear headshot, full body shot, and variety of angles. ({photos.length}/6)</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[0, 1, 2, 3, 4, 5].map((slot) => {
              const photo = photos[slot];
              const labels = ['Headshot', 'Full Body', 'Profile Left', 'Profile Right', 'Three-Quarter', 'Additional'];
              return (
                <div key={slot} className="relative group" data-testid={`photo-slot-${slot}`}>
                  {photo ? (
                    <div className="relative aspect-[3/4] rounded-xl overflow-hidden border border-white/10">
                      <img
                        src={getFileUrl(photo.storage_path)}
                        alt={labels[slot]}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={() => handleDeleteFile(photo.id, 'image')}
                          className="p-2 bg-red-500/80 rounded-full text-white hover:bg-red-500"
                          data-testid={`delete-photo-${slot}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                        <p className="text-white text-xs font-medium">{labels[slot]}</p>
                      </div>
                    </div>
                  ) : (
                    <label className="aspect-[3/4] rounded-xl border-2 border-dashed border-white/15 bg-[#0A0A0A] flex flex-col items-center justify-center cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e, slot)}
                        disabled={uploadingSlot !== null}
                        data-testid={`upload-photo-${slot}`}
                      />
                      {uploadingSlot === slot ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-white/20 mb-2" />
                          <p className="text-white/40 text-sm font-medium">{labels[slot]}</p>
                          <p className="text-white/20 text-xs mt-1">Click to upload</p>
                        </>
                      )}
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-2">Required Documents</h2>
            <p className="text-white/40 text-sm mb-6">Upload passport, right to work visa, and other required identification</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { type: 'passport', label: 'Passport', desc: 'Photo page of your passport' },
                { type: 'right_to_work', label: 'Right to Work', desc: 'Visa or share code proof' },
                { type: 'other', label: 'Other ID', desc: 'Driving licence, proof of address, etc.' }
              ].map((docSlot) => {
                const existingDocs = documents.filter(d => d.doc_type === docSlot.type);
                return (
                  <div key={docSlot.type} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4" data-testid={`doc-slot-${docSlot.type}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-5 h-5 text-[#D4AF37]" />
                      <h3 className="text-white font-medium">{docSlot.label}</h3>
                    </div>
                    <p className="text-white/30 text-xs mb-4">{docSlot.desc}</p>
                    {existingDocs.length > 0 ? (
                      <div className="space-y-2 mb-3">
                        {existingDocs.map(doc => (
                          <div key={doc.id} className="flex items-center justify-between bg-[#121212] rounded-lg p-2">
                            <span className="text-white/60 text-sm truncate flex-1">{doc.original_filename}</span>
                            <button onClick={() => handleDeleteFile(doc.id, 'document')} className="text-red-400 hover:text-red-300 ml-2">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : null}
                    <label className="flex items-center justify-center gap-2 p-3 border border-dashed border-white/15 rounded-lg cursor-pointer hover:border-[#D4AF37]/50 transition-colors">
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        className="hidden"
                        onChange={(e) => handleDocUpload(e, docSlot.type)}
                        disabled={uploadingDoc !== null}
                        data-testid={`upload-doc-${docSlot.type}`}
                      />
                      {uploadingDoc === docSlot.type ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#D4AF37]"></div>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 text-white/30" />
                          <span className="text-white/40 text-sm">{existingDocs.length > 0 ? 'Upload another' : 'Upload file'}</span>
                        </>
                      )}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Experience Tab */}
      {activeTab === 'experience' && (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-['Playfair_Display'] text-xl font-bold text-white">Stand-In Experience</h2>
              <p className="text-white/40 text-sm mt-1">List productions you've worked on as a stand-in</p>
            </div>
            <Button
              variant="outline"
              onClick={addExperience}
              className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="add-experience-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Production
            </Button>
          </div>

          {experiences.length === 0 ? (
            <div className="text-center py-12 bg-[#0A0A0A] rounded-lg border border-dashed border-white/10">
              <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40">No experience added yet</p>
              <p className="text-white/30 text-sm mt-1">Click "Add Production" to add your work history</p>
            </div>
          ) : (
            <div className="space-y-4">
              {experiences.map((exp, index) => (
                <div key={index} className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[#D4AF37] font-medium">Production #{index + 1}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExperience(index)}
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/60 text-sm mb-1 block">Production Name</Label>
                      <Input
                        value={exp.production_name}
                        onChange={(e) => updateExperience(index, 'production_name', e.target.value)}
                        placeholder="e.g., Deadpool & Wolverine"
                        className="bg-[#121212] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-sm mb-1 block">Actor You Stood In For</Label>
                      <Input
                        value={exp.actor_name}
                        onChange={(e) => updateExperience(index, 'actor_name', e.target.value)}
                        placeholder="e.g., Ryan Reynolds"
                        className="bg-[#121212] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-sm mb-1 block">Crowd 2nd Name</Label>
                      <Input
                        value={exp.crowd_2nd}
                        onChange={(e) => updateExperience(index, 'crowd_2nd', e.target.value)}
                        placeholder="Name of Crowd 2nd"
                        className="bg-[#121212] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/60 text-sm mb-1 block">Days Worked</Label>
                      <Input
                        type="number"
                        value={exp.days_worked}
                        onChange={(e) => updateExperience(index, 'days_worked', parseInt(e.target.value) || 1)}
                        placeholder="1"
                        className="bg-[#121212] border-white/10 text-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {/* Payment & ID Tab */}
      {activeTab === 'payment' && (
        <div className="space-y-6">
          <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-6">Contact & Personal Details</h2>
            <p className="text-white/40 text-sm mb-4">This information appears on your daily chit and is required for bookings</p>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-white/80 mb-2 block">Phone Number *</Label>
                  <Input
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="+44 7700 000000"
                    className="bg-[#0A0A0A] border-white/10 text-white h-12"
                    data-testid="profile-phone"
                  />
                </div>
                <div>
                  <Label className="text-white/80 mb-2 block">Email *</Label>
                  <Input
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-[#0A0A0A] border-white/10 text-white h-12"
                    data-testid="profile-email"
                  />
                </div>
                <div>
                  <Label className="text-white/80 mb-2 block">Date of Birth *</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="bg-[#0A0A0A] border-white/10 text-white h-12"
                    data-testid="profile-dob"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Address *</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address including postcode"
                  rows={3}
                  className="bg-[#0A0A0A] border-white/10 text-white resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/80 mb-2 block">Emergency Contact Name</Label>
                  <Input
                    value={formData.emergency_contact_name}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                    placeholder="Full name"
                    className="bg-[#0A0A0A] border-white/10 text-white h-12"
                  />
                </div>
                <div>
                  <Label className="text-white/80 mb-2 block">Emergency Contact Phone</Label>
                  <Input
                    value={formData.emergency_contact_phone}
                    onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                    placeholder="+44 7700 000000"
                    className="bg-[#0A0A0A] border-white/10 text-white h-12"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-6">Payment Details</h2>
            <p className="text-white/40 text-sm mb-4">Required for receiving payment from productions</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80 mb-2 block">National Insurance Number *</Label>
                <Input
                  value={formData.national_insurance}
                  onChange={(e) => setFormData({ ...formData, national_insurance: e.target.value })}
                  placeholder="AB 12 34 56 C"
                  className="bg-[#0A0A0A] border-white/10 text-white h-12"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Bank Name *</Label>
                <Input
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="e.g., Barclays"
                  className="bg-[#0A0A0A] border-white/10 text-white h-12"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Account Number *</Label>
                <Input
                  value={formData.account_number}
                  onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                  placeholder="12345678"
                  className="bg-[#0A0A0A] border-white/10 text-white h-12"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Sort Code *</Label>
                <Input
                  value={formData.sort_code}
                  onChange={(e) => setFormData({ ...formData, sort_code: e.target.value })}
                  placeholder="12-34-56"
                  className="bg-[#0A0A0A] border-white/10 text-white h-12"
                />
              </div>
            </div>
          </div>

          <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
            <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-4">Right to Work</h2>
            <div className="bg-[#0A0A0A] rounded-lg p-4 border border-white/5">
              <p className="text-white/60 text-sm">Please upload your right to work documents (passport, visa) in the <button onClick={() => setActiveTab('documents')} className="text-[#D4AF37] underline">Documents</button> tab.</p>
              {documents.filter(d => d.doc_type === 'right_to_work' || d.doc_type === 'passport').length > 0 && (
                <p className="text-[#10B981] text-sm mt-2">Documents uploaded</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Availability Manager Component
const AvailabilityManager = ({ token }) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchAvailability();
  }, []);

  const fetchAvailability = async () => {
    try {
      const response = await axios.get(`${API}/standin/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const unavail = (response.data.unavailable_dates || []).map(d => new Date(d + 'T00:00:00'));
      setUnavailableDates(unavail);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoaded(true);
    }
  };

  const formatDate = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const markUnavailable = async () => {
    if (selectedDates.length === 0) return;
    const newUnavail = [...unavailableDates];
    for (const d of selectedDates) {
      const ds = formatDate(d);
      if (!newUnavail.find(u => formatDate(u) === ds)) newUnavail.push(d);
    }
    setUnavailableDates(newUnavail);
    await saveAvailability(newUnavail);
    setSelectedDates([]);
  };

  const clearUnavailable = async () => {
    if (selectedDates.length === 0) return;
    const newUnavail = unavailableDates.filter(u => {
      const us = formatDate(u);
      return !selectedDates.find(d => formatDate(d) === us);
    });
    setUnavailableDates(newUnavail);
    await saveAvailability(newUnavail);
    setSelectedDates([]);
  };

  const saveAvailability = async (unavail) => {
    setSaving(true);
    try {
      await axios.put(`${API}/standin/availability`, {
        available_dates: [],
        unavailable_dates: unavail.map(formatDate)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Availability saved');
    } catch (error) {
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const isDayUnavailable = (day) => unavailableDates.some(d => formatDate(d) === formatDate(day));

  return (
    <div className="max-w-4xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Availability</h1>
      <p className="text-white/60 mb-8">Mark dates when you are NOT available. All other dates are assumed available.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <h2 className="font-semibold text-white mb-4">Calendar</h2>
          <Calendar
            mode="multiple"
            selected={selectedDates}
            onSelect={setSelectedDates}
            className="rounded-md border border-white/10"
            modifiers={{
              unavailable: unavailableDates,
            }}
            modifiersClassNames={{
              unavailable: 'bg-[#EF4444]/30 text-white rounded-md',
            }}
            data-testid="availability-calendar"
          />
          {selectedDates.length > 0 && (
            <p className="text-white/40 text-sm mt-3">{selectedDates.length} date(s) selected</p>
          )}
        </div>

        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <h2 className="font-semibold text-white mb-4">Actions</h2>
          <div className="space-y-4">
            <Button 
              onClick={markUnavailable}
              disabled={saving || selectedDates.length === 0}
              className="w-full bg-[#EF4444] hover:bg-[#EF4444]/80 text-white font-semibold"
              data-testid="mark-unavailable-btn"
            >
              Mark as Unavailable ({selectedDates.length})
            </Button>
            <Button 
              onClick={clearUnavailable}
              disabled={saving || selectedDates.length === 0}
              variant="outline"
              className="w-full border-white/20 text-white/60 hover:text-white"
              data-testid="clear-unavailable-btn"
            >
              Remove Unavailable Mark ({selectedDates.length})
            </Button>
          </div>
          
          <div className="mt-8">
            <h3 className="text-white/60 text-sm mb-2">Legend</h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-[#EF4444]"></div>
                <span className="text-white/60 text-sm">Unavailable ({unavailableDates.length} days)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-white/10 border border-white/20"></div>
                <span className="text-white/60 text-sm">Available (all other dates)</span>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-[#0A0A0A] rounded-lg">
            <p className="text-white/40 text-sm">
              You only need to mark dates when you are NOT available. All unmarked dates are treated as available for bookings.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Bookings View Component
const BookingsView = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">My Bookings</h1>
      <p className="text-white/60 mb-8">View and manage your bookings</p>

      {bookings.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No bookings yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((booking) => (
            <div key={booking.id} className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold">{booking.production_name || 'Production'}</h3>
                  <div className="flex items-center gap-4 mt-2 text-white/60 text-sm">
                    <span className="flex items-center gap-1">
                      <CalendarIcon className="w-4 h-4" />
                      {booking.dates?.join(', ') || 'TBD'}
                    </span>
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      £{booking.rate}/day
                    </span>
                  </div>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                  booking.status === 'confirmed' ? 'bg-[#10B981]/20 text-[#10B981]' :
                  booking.status === 'pending' ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                  'bg-white/10 text-white/60'
                }`}>
                  {booking.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Productions View Component
const ProductionsView = ({ token }) => {
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProductions();
  }, []);

  const fetchProductions = async () => {
    try {
      const response = await axios.get(`${API}/standin/productions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductions(response.data);
    } catch (error) {
      console.error('Error fetching productions:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Productions</h1>
      <p className="text-white/60 mb-8">Your work history</p>

      {productions.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <Briefcase className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No productions yet</p>
        </div>
      ) : (
        <div className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="productions-table">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left p-4 text-white/60 text-sm font-medium">Production</th>
                <th className="text-left p-4 text-white/60 text-sm font-medium">Actor</th>
                <th className="text-left p-4 text-white/60 text-sm font-medium">Days</th>
              </tr>
            </thead>
            <tbody>
              {productions.map((prod, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-4">
                    <p className="text-white font-medium">{prod.production_name || '-'}</p>
                  </td>
                  <td className="p-4 text-white/60">{prod.actor_name || '-'}</td>
                  <td className="p-4">
                    <span className="text-[#D4AF37] font-medium">{prod.days_worked || '-'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Settings View Component
const SettingsView = () => {
  const { user } = useAuth();
  
  return (
    <div className="max-w-2xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Settings</h1>
      <p className="text-white/60 mb-8">Manage your account settings</p>

      <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
        <h2 className="font-semibold text-white mb-4">Account Information</h2>
        <div className="space-y-4">
          <div>
            <Label className="text-white/60 text-sm">Email</Label>
            <p className="text-white">{user?.email}</p>
          </div>
          <div>
            <Label className="text-white/60 text-sm">Account Status</Label>
            <p className={`capitalize ${user?.status === 'approved' ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>
              {user?.status}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StandInDashboard;
